import { DurationSettings } from "@oculai/planner/dist/services/durationService";
import { endOfDay, isWithinInterval, millisecondsToHours, startOfDay } from "date-fns";
import { Column, Data } from "@/components/UI/Table/types";
import { ShortenedProcess } from "@/types/Process";

export const filterColumnDateField = (value: unknown, item: Data, field?: string) => {
  if (!value) {
    return true;
  }

  const [start, end] = value as [Date, Date];

  if (!field) {
    const isValid = !!item.subData?.some((process) =>
      isWithinInterval(process.date as Date, { start: startOfDay(start), end: endOfDay(end) }),
    );

    return isValid;
  }

  const isValid = isWithinInterval(item[field] as Date, {
    start: startOfDay(start),
    end: endOfDay(end),
  });

  return isValid;
};

export const filterComboboxField = (v: unknown, item: Data, column: Column) => {
  const value = v as string[];

  if (!value.length) {
    return true;
  }
  return value.includes(item[column.field] as string);
};

export const filterProcessField = (v: unknown, item: Data): boolean => {
  const value = v as Record<"processTypes" | "processes", string[]>;

  if (value.processes && value.processes.length === 0) {
    return true;
  }
  return (
    Array.isArray(value.processes) &&
    value.processes.includes(
      item?.encoded_label !== undefined && item?.encoded_label !== null
        ? (item?.encoded_label.toString() as string)
        : "",
    )
  );
};

export const filterLocationField = (v: unknown, item: Data) => {
  const value = v as Record<string, string[]>;
  for (const key in value) {
    if (!value[key].length) {
      continue;
    }

    if (!value[key].some((location) => location === item[key])) {
      return false;
    }
  }

  return true;
};

export const calculateActiveDuration = (
  durationSettings: DurationSettings,
  processes: ShortenedProcess[],
) => {
  processes.sort((a, b) => a.start_time.getTime() - b.start_time.getTime());

  const intervals: Record<string, [Date, Date][]> = {};
  let dayCount = 0;
  let timeCount = 0;
  let lastDate = "";

  const calculateLastIntervalDuration = (workIntervals: [Date, Date][]) => {
    let lastTime = 0;

    const duration = workIntervals
      .sort((a, b) => a[0].getTime() - b[0].getTime())
      .reduce((acc, interval) => {
        const start = interval[0].getTime();
        const end = interval[1].getTime();

        if (lastTime > start && lastTime < end) {
          lastTime = end;
          return acc + end - lastTime;
        }

        if (lastTime < start) {
          lastTime = end;
          return acc + end - start;
        }

        return acc;
      }, 0);

    return duration;
  };

  processes.forEach((process) => {
    const date = process.start_time.toLocaleDateString();

    if (!intervals[date]) {
      const weekDay = process.start_time.getDay();
      const workDay = durationSettings.workingDaysAndHoursMap[weekDay].workingDays;

      if (lastDate) {
        timeCount += calculateLastIntervalDuration(intervals[lastDate]);
      }

      intervals[date] = [];
      lastDate = date;
      dayCount += workDay;
    }

    const workIntervals = process.work_intervals.map<[Date, Date]>((interval) => [
      interval.start_time,
      interval.end_time,
    ]);

    intervals[date].push(...workIntervals);
  });

  if (lastDate) {
    timeCount += calculateLastIntervalDuration(intervals[lastDate]);
  }

  return {
    days: dayCount,
    hours: millisecondsToHours(timeCount) || 1,
  };
};

export const calculateWorkHoursSum = (processes: ShortenedProcess[], decimalHours = false) => {
  const workTime = processes.reduce((accProcesses, process) => {
    const processWorkHours = process.work_intervals.reduce((accIntervals, interval) => {
      const period =
        (interval.end_time.getTime() - interval.start_time.getTime()) *
        (interval.workforce?.validated_count || 0);

      return accIntervals + period;
    }, 0);

    return accProcesses + processWorkHours;
  }, 0);

  return decimalHours ? millisecondsToDecimalHours(workTime) : millisecondsToHours(workTime);
};

const millisecondsToDecimalHours = (milliseconds: number) => {
  const hours = milliseconds / (1000 * 60 * 60);
  return parseFloat(hours.toFixed(2));
};
